/*!
 * Copyright © 2018-2021. Verizon Connect Ireland Limited. All rights reserved.
 */

import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store, select } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, map, switchMap, withLatestFrom } from 'rxjs/operators';

import { isArrayNullOrEmpty, isElementNull } from '../../../../utils';
import { EPlotType, EStartLocationSource, IAssetPlot, IVehiclePlot, LatLng, MarkerInfo } from '../../models';
import {
  CloseBalloon,
  EAssetsActions,
  EBalloonInfoActions,
  ELayoutActions,
  EMapPlotsActions,
  EMapsActions,
  GetAssetPlotsSuccess,
  GetMapBoundsFromUserPlotsSuccess,
  GetVehiclePlotsSuccess,
  HighlightMarker,
  HighlightMarkerSuccess,
  RemoveSelectedMarker,
  SetSelectedMarker,
  SetSelectedMarkerSuccess,
  ShowStartingLocation,
  UpdateStreetViewLocation,
  UpdateVehicleBalloon,
  UpdateZoomToMarker
} from '../actions';
import { SelectVehicleIdForDeviceHealth } from '../actions/device-health.actions';
import { getIsStreetViewOpen, getMapChangeBounds, getMarkersToHighlight } from '../selectors';
import { IAppState } from '../state';

@Injectable()
export class MapsEffects {
  public onGetVehiclePlotsSuccessShowStartLocation$ = createEffect(() =>
    this._actions$.pipe(
      ofType<GetVehiclePlotsSuccess | GetAssetPlotsSuccess>(EMapPlotsActions.GetVehiclePlotsSuccess, EAssetsActions.GetAssetPlotsSuccess),
      withLatestFrom(this._store.pipe(select(getMapChangeBounds))),
      filter(([, mapBounds]) => isElementNull(mapBounds)),
      map(([action]) => action.payload),
      map(plots =>
        this._isAssetPlots(plots)
          ? plots.map(p => new LatLng(p.center.Latitude, p.center.Longitude))
          : plots.map(p => new LatLng(p.coordinates.lat, p.coordinates.lng))
      ),
      switchMap(latLngs => {
        const startLocationSource = isArrayNullOrEmpty(latLngs)
          ? EStartLocationSource.FromUserSettings
          : EStartLocationSource.FromUserPlots;
        return [new GetMapBoundsFromUserPlotsSuccess(latLngs), new ShowStartingLocation(startLocationSource)];
      })
    )
  );

  public updateStreetViewLocationWhenZoomToMarker$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType<UpdateZoomToMarker>(ELayoutActions.UpdateZoomToMarker),
      map(action => action.payload),
      withLatestFrom(this._store.pipe(select(getIsStreetViewOpen))),
      filter(([, isStreetViewOpen]) => isStreetViewOpen),
      map(([params]) => new UpdateStreetViewLocation(params.latLng))
    )
  );

  public highlightMarker$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType<HighlightMarker>(EMapsActions.HighlightMarker),
      map(action => action.payload),
      withLatestFrom(this._store.pipe(select(getMarkersToHighlight))),
      map(([id, markers]: [number, MarkerInfo[]]) => markers.find(marker => marker.id === id)),
      filter(marker => !isElementNull(marker)),
      map((marker: MarkerInfo) => new HighlightMarkerSuccess(marker))
    )
  );

  public setSelectedMarker$ = createEffect(() =>
    this._actions$.pipe(
      ofType<SetSelectedMarker>(EMapsActions.SetSelectedMarker),
      map(action => action.payload),
      withLatestFrom(this._store.pipe(select(getMarkersToHighlight))),
      map(([id, markers]: [number, MarkerInfo[]]) => markers.find(marker => marker.id === id)),
      filter(marker => !isElementNull(marker)),
      switchMap(marker => {
        const actions: Action[] = [new SetSelectedMarkerSuccess(marker)];
        if (marker.type == EPlotType.vehicle) {
          actions.push(new SelectVehicleIdForDeviceHealth(marker.id.toString()));
        }
        return actions;
      })
    )
  );

  public removeSelectedMarker$ = createEffect(() =>
    this._actions$.pipe(
      ofType<CloseBalloon | UpdateVehicleBalloon>(EBalloonInfoActions.CloseBalloon, EBalloonInfoActions.UpdateVehicleBalloon),
      map(() => new RemoveSelectedMarker())
    )
  );

  constructor(
    private readonly _actions$: Actions,
    private readonly _store: Store<IAppState>
  ) {}

  private _isAssetPlots(plots: IVehiclePlot[] | IAssetPlot[]): plots is IAssetPlot[] {
    return plots.length > 0 && (plots[0] as IAssetPlot).center !== undefined;
  }
}
